import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Device } from "@twilio/voice-sdk";
import { Phone, PhoneOff, PhoneIncoming } from "lucide-react";
import api from "../lib/api";
import { syncPush } from "../lib/push";
import { prettyNum } from "../components/NativeContacts";

const PhoneCtx = createContext(null);
export const usePhone = () => useContext(PhoneCtx);

const micError = (e) => /permission|notallowed|31208|31402/i.test(`${e?.name} ${e?.code} ${e?.message}`)
  ? "Microphone access is blocked. Allow the microphone for Lexarra in your browser settings, then try again." : (e?.message || "Phone error");

function useToneMixer() {
  const m = useRef({ ctx: null, dest: null, mic: null, buf: {}, raw: {}, busy: Promise.resolve() });
  const processor = {
    async createProcessedStream(stream) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream); const mic = ctx.createGain(); const dest = ctx.createMediaStreamDestination();
      src.connect(mic); mic.connect(dest);
      Object.assign(m.current, { ctx, dest, mic });
      return dest.stream;
    },
    async destroyProcessedStream() { m.current.ctx?.close().catch(() => {}); Object.assign(m.current, { ctx: null, dest: null, mic: null }); },
  };
  const load = async (kind = "started") => {
    if (!m.current.raw[kind]) m.current.raw[kind] = (await api.get("/firm/native-phone/recording-tone/audio", { params: { kind }, responseType: "arraybuffer" })).data;
    return m.current.raw[kind];
  };
  // Mixes the platform tone into the live call's outgoing audio (the other party hears it; it is recorded) and plays it on this device's speaker.
  // Tones are serialised so a stopped tone never overlaps a started tone.
  const play = (call, kind = "started") => { const p = m.current.busy.then(() => playNow(call, kind)); m.current.busy = p.catch(() => {}); return p; };
  const playNow = async (call, kind) => {
    const { ctx, dest, mic } = m.current;
    if (!ctx || !dest) throw new Error("no live audio");
    if (ctx.state === "suspended") await ctx.resume();
    const cached = m.current.buf[kind];
    if (!cached || cached.ctx !== ctx) { const b = await ctx.decodeAudioData((await load(kind)).slice(0)); m.current.buf[kind] = { b, ctx }; }
    const buf = m.current.buf[kind].b;
    const wasMuted = call?.isMuted?.();
    if (wasMuted) { mic.gain.value = 0; call.mute(false); }
    const out = ctx.createBufferSource(); out.buffer = buf; out.connect(dest);
    const local = ctx.createBufferSource(); local.buffer = buf; const lg = ctx.createGain(); lg.gain.value = 0.6; local.connect(lg); lg.connect(ctx.destination);
    await new Promise((ok) => { out.onended = ok; out.start(); local.start(); });
    if (wasMuted) { call.mute(true); mic.gain.value = 1; }
  };
  return { processor, play, load };
}

function useDevice(enabled, autoRef, onIncoming) {
  const dev = useRef(null);
  const tone = useToneMixer();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("connecting");
  const [error, setError] = useState("");
  useEffect(() => {
    if (!enabled) { setState("unavailable"); return undefined; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/firm/native-phone/dialer/token");
        if (!alive) return;
        setInfo(data);
        const d = new Device(data.token, { codecPreferences: ["opus", "pcmu"], closeProtection: true, allowIncomingWhileBusy: false });
        d.on("registered", () => setState("ready"));
        d.on("unregistered", () => setState("offline"));
        d.on("error", (e) => setError(micError(e)));
        d.on("incoming", (c) => { if (!autoRef.current?.(c)) onIncoming(c); });
        d.on("tokenWillExpire", async () => { const r = await api.get("/firm/native-phone/dialer/token"); d.updateToken(r.data.token); });
        dev.current = d;
        try { await d.audio.addProcessor(tone.processor); tone.load("started").catch(() => {}); tone.load("stopped").catch(() => {}); } catch (e) { console.warn("recording tone mixer unavailable", e); }
        await d.register();
        syncPush();
      } catch (e) { if (alive) { setState("unavailable"); setError(e.response?.data?.detail || e.message || "Phone unavailable"); } }
    })();
    return () => { alive = false; dev.current?.destroy(); dev.current = null; };
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return { dev, info, state, error, setError, playTone: tone.play };
}

export function IncomingSheet({ inc, onAccept, onReject }) {
  const via = inc.c.customParameters?.get?.("lx_from");
  const from = inc.c.parameters?.From;
  return (
    <div className="fixed inset-0 z-[160] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="dialer-incoming">
      <div className="w-full max-w-sm surface hairline rounded-3xl p-6 text-center fade-up">
        <span className="relative mx-auto w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center"><span className="absolute inset-0 rounded-full bg-emerald-500/25 animate-ping" /><PhoneIncoming size={24} className="text-emerald-500 relative" /></span>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-4" data-testid="dialer-incoming-label">{via ? `Transferred from ${via}` : "Incoming call"}</div>
        {inc.name && <div className="text-2xl font-semibold mt-1 truncate" data-testid="dialer-incoming-name">{inc.name}</div>}
        <div className={`font-mono-legal mt-1 ${inc.name ? "text-[15px] text-muted-foreground" : "text-2xl"}`} data-testid="dialer-incoming-from">{from ? prettyNum(from) : "Unknown caller"}</div>
        {inc.line && <div className="text-[12px] text-muted-foreground mt-2" data-testid="dialer-incoming-line">To your line {prettyNum(inc.line)}</div>}
        <div className="flex justify-center gap-12 mt-7">
          <div className="flex flex-col items-center gap-1.5"><button onClick={onReject} data-testid="dialer-reject-btn" className="w-16 h-16 rounded-full bg-red-500 text-white flex items-center justify-center active:scale-95 transition-transform"><PhoneOff size={24} /></button><span className="text-[11px] text-muted-foreground">Decline</span></div>
          <div className="flex flex-col items-center gap-1.5"><button onClick={onAccept} data-testid="dialer-accept-btn" className="w-16 h-16 rounded-full bg-emerald-500 text-white flex items-center justify-center active:scale-95 transition-transform"><Phone size={24} /></button><span className="text-[11px] text-muted-foreground">Answer</span></div>
        </div>
      </div>
    </div>
  );
}

export function PhoneProvider({ enabled, children }) {
  const nav = useNavigate();
  const autoRef = useRef(null);
  const answerRef = useRef(null);
  const pending = useRef(null);
  const [incoming, setIncoming] = useState(null);
  const onIncoming = async (c) => {
    const clear = () => setIncoming((x) => (x && x.c === c ? null : x));
    c.on("cancel", clear); c.on("disconnect", clear); c.on("reject", clear);
    const line = c.customParameters?.get?.("lx_line"); const ring = c.customParameters?.get?.("lx_ring");
    setIncoming({ c, name: null, line, ring });
    try { const r = await api.get("/firm/native-phone/lookup", { params: { number: c.parameters?.From || "" } }); setIncoming((x) => (x && x.c === c ? { ...x, name: r.data.name } : x)); } catch { /* caller ID only */ }
  };
  const device = useDevice(enabled, autoRef, onIncoming);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const h = (e) => { if (e.data?.lx === "open-url") { const u = new URL(e.data.url); nav(u.pathname + u.search); } };
    navigator.serviceWorker.addEventListener("message", h);
    return () => { navigator.serviceWorker.removeEventListener("message", h); };
  }, [nav]);
  const accept = () => {
    const inc = incoming; setIncoming(null);
    if (answerRef.current) { answerRef.current(inc); return; }
    pending.current = inc; nav("/phone-dialer");
  };
  const reject = () => { try { incoming?.c.reject(); } catch { /* already gone */ } setIncoming(null); };
  const takePending = () => { const p = pending.current; pending.current = null; return p; };
  return (
    <PhoneCtx.Provider value={{ ...device, enabled, autoRef, answerRef, takePending, incoming }}>
      {children}
      {incoming && <IncomingSheet inc={incoming} onAccept={accept} onReject={reject} />}
    </PhoneCtx.Provider>
  );
}
